// profileService.js - All Profile Operations
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// ============================================
// PROFILE CRUD OPERATIONS
// ============================================

export const getProfile = async (userId) => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'data');
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      console.log('📦 Profile loaded:', profileSnap.data());
      return profileSnap.data();
    }
    
    console.log('⚠️ No profile found for user:', userId);
    return null;
  } catch (error) {
    console.error('❌ Error getting profile:', error);
    throw error;
  }
};

export const createProfile = async (userId, profileData) => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'data');
    await setDoc(profileRef, {
      ...profileData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    console.log('✅ Profile created successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error creating profile:', error);
    throw error;
  }
};

export const updateProfile = async (userId, updates) => {
  try {
    const profileRef = doc(db, 'users', userId, 'profile', 'data');
    await updateDoc(profileRef, {
      ...updates,
      updatedAt: new Date()
    });
    
    console.log('✅ Profile updated successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Error updating profile:', error);
    throw error;
  }
};

// ============================================
// PROFILE VALIDATION
// ============================================

export const isProfileComplete = (profile) => {
  if (!profile) return false;
  
  const checks = {
    hasArtistName: !!profile.artistName,
    hasFullName: !!profile.fullName,
    hasGenre: !!profile.genre,
    hasLocation: !!profile.location,
    hasBio: !!profile.bio
  };
  
  console.log('🔍 isProfileComplete - checks:', checks);
  
  const isComplete = Object.values(checks).every(check => check === true);
  console.log('🔍 isProfileComplete - result:', isComplete);
  
  return isComplete;
};

// ============================================
// ARTIST SEARCH
// ============================================

export const searchArtists = async (searchTerm, filters = {}) => {
  try {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    const artists = [];
    
    for (const userDoc of usersSnapshot.docs) {
      try {
        const profileRef = doc(db, 'users', userDoc.id, 'profile', 'data');
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          const profile = profileSnap.data();
          
          // Only include users with artistName (completed artist profile)
          if (profile.artistName) {
            const artistName = profile.artistName.toLowerCase();
            const fullName = (profile.fullName || '').toLowerCase();
            const searchLower = searchTerm.toLowerCase().trim();
            
            // Check if matches search
            if (artistName.includes(searchLower) || fullName.includes(searchLower)) {
              artists.push({
                id: userDoc.id,
                artistName: profile.artistName,
                fullName: profile.fullName || '',
                genre: profile.genre || '',
                location: profile.location || '',
                profilePhoto: profile.profilePhoto || '',
                bio: profile.bio || '',
                socialMedia: profile.socialMedia || {},
                socialStats: profile.socialStats || {},
                rating: profile.rating || 0,
                totalGigs: profile.totalGigs || 0
              });
            }
          }
        }
      } catch (profileError) {
        console.log('⚠️ No profile for user:', userDoc.id);
      }
    }
    
    // Sort by relevance
    artists.sort((a, b) => {
      const aNameLower = a.artistName.toLowerCase();
      const bNameLower = b.artistName.toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      // Exact match first
      if (aNameLower === searchLower) return -1;
      if (bNameLower === searchLower) return 1;
      
      // Starts with search term
      if (aNameLower.startsWith(searchLower) && !bNameLower.startsWith(searchLower)) return -1;
      if (bNameLower.startsWith(searchLower) && !aNameLower.startsWith(searchLower)) return 1;
      
      // Alphabetical
      return aNameLower.localeCompare(bNameLower);
    });
    
    return artists;
  } catch (error) {
    console.error('❌ Error searching artists:', error);
    throw error;
  }
};

// ============================================
// PUBLIC ARTIST PROFILE
// ============================================

export const getArtistProfile = async (artistId) => {
  try {
    return await getProfile(artistId);
  } catch (error) {
    console.error('❌ Error getting artist profile:', error);
    throw error;
  }
};
